import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { Card } from "./MobileShell";
import { coachHistory, coachChat, coachClear } from "../lib/api";

const STARTERS = ["Slept badly — adjust today's training?", "High-protein lunch ideas", "How do I close all 6 pillars?"];

export default function AICoachCard({ token }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    coachHistory(token)
      .then((d) => setMsgs(Array.isArray(d) ? d.filter((m) => m && typeof m.text === "string") : []))
      .catch(() => setErr("Could not load conversation"));
  }, [token]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [msgs, busy]);

  const send = async (m) => {
    const message = (m ?? text).trim();
    if (!message || busy) return;
    setErr("");
    setBusy(true);
    setText("");
    setMsgs((x) => [...x, { id: "pending", role: "user", text: message }]);
    try {
      const r = await coachChat(token, message);
      setMsgs((x) => [...x.filter((y) => y.id !== "pending"), ...[r?.user, r?.reply].filter((m) => m && typeof m.text === "string")]);
    } catch (e) {
      setMsgs((x) => x.filter((y) => y.id !== "pending"));
      setText(message);
      setErr(e?.response?.data?.detail || "Coach is offline — check your connection and retry");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear your AI coach conversation?")) return;
    await coachClear(token).catch(() => null);
    setMsgs([]);
  };

  return (
    <Card className="mb-4" data-testid="ai-coach-card">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow text-[var(--lc-moss)] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> AI Coach · 24/7
        </div>
        {msgs.length > 0 && (
          <button onClick={clear} className="text-[var(--lc-muted)]" data-testid="ai-coach-clear" title="Clear">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto space-y-2 pr-1" data-testid="ai-coach-messages">
        {msgs.length === 0 && (
          <div className="flex flex-wrap gap-1.5">
            {STARTERS.map((s) => (
              <button key={s} onClick={() => send(s)} className="px-2.5 py-1.5 text-[11px] border border-[var(--lc-line)] rounded-full" data-testid="ai-coach-starter">
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={m.id + i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div
              className={`max-w-[85%] px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap rounded-2xl ${
                m.role === "user" ? "bg-[var(--lc-ink)] text-[var(--lc-cream)] rounded-br-sm" : "bg-[var(--lc-cream)] rounded-bl-sm"
              }`}
              data-testid={`ai-coach-msg-${m.role}`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-[var(--lc-muted)]" data-testid="ai-coach-typing" />}
        <div ref={endRef} />
      </div>
      {err && <div className="mt-2 text-[11px] text-red-700" data-testid="ai-coach-error">{err}</div>}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask your coach…"
          maxLength={2000}
          className="flex-1 px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-md text-sm"
          data-testid="ai-coach-input"
        />
        <button disabled={busy || !text.trim()} className="px-3 bg-[var(--lc-moss)] text-white rounded-md disabled:opacity-50" data-testid="ai-coach-send">
          <Send className="h-4 w-4" />
        </button>
      </form>
      <p className="mt-2 text-[10px] text-[var(--lc-muted)]">General wellness guidance — not medical advice.</p>
    </Card>
  );
}
